import { BaseCampaignItem, CampaignBase, CampaignFilterType } from '@retail/campaign/types';
import {
  CampaignFilterChips,
  CampaignFilters,
  CampaignsTable,
  CampaignToolbar,
  CampaignTypeOption,
} from '@retail/campaign/components';
import { useLanguageSelector } from '@retail/app/hooks';
import { useCallback, useEffect, useState } from 'react';
import { useDisclosure } from '@shared/hooks';
import { CreateBrandCampaignContainer, CreateMemberCampaignContainer } from './CreateCampaign';
import { Box, Stack, Typography } from '@mui/material';
import { useSelectedOrgUnit, useUserBrands } from '@retail/app/stores/selected-context';
import { CampaignDeleteConfirmationContainer } from './CampaignDeleteConfirmationContainer';
import {
  DiagonalMosaicImgBg,
  ToggleFiltersToolbar,
  ToggleFiltersToolbarButton,
} from '@retail/components';
import { useAppTFunction } from '@retail/app/i18n';
import { Paper } from '@shared/components';
import { useCampaignsFilters } from './useCampaignsFilters';
import { useToast } from '@retail/hooks';
import { GridColDef } from '@mui/x-data-grid';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { CampaignTenantType } from '@retail/campaign/data-access';
import { Action } from '@retail/auth/types';

export type CampaignAction = 'Delete';

interface ActionState {
  campaign: CampaignBase<any>;
  action: CampaignAction;
}

interface Props {
  campaigns: CampaignBase<any>[];
  status: 'loading' | 'error' | 'success';
  isLoading: boolean;
  campaignsTableColumns: GridColDef[];
  availableFilters: CampaignFilterType[];
  type: CampaignTenantType;
  campaignActions: Action[];
}
const campaignTypeOptionsMap: Record<CampaignTenantType, CampaignTypeOption[]> = {
  brand: ['ALL', 'CENTRAL', 'OPTIONAL', 'SUPPLIER'],
  member: ['ALL', 'CENTRAL', 'OPTIONAL', 'SUPPLIER', 'LOCAL'],
};

export const CampaignContainer = ({
  campaigns,
  status,
  isLoading,
  campaignsTableColumns,
  availableFilters,
  type,
  campaignActions,
}: Props) => {
  const t = useAppTFunction();
  const { language } = useLanguageSelector();
  const toast = useToast();
  const canCreateCampaign = campaignActions.includes('CREATE');
  const { isBrandContext } = useSelectedOrgUnit();

  const { availableBrandsForChosenTenant } = useUserBrands();

  const {
    datePreset,
    setDatePreset,
    searchQuery,
    setSearchQuery,
    campaignType: campaignTypeFilter,
    setCampaignType: setCampaignTypeFilter,
    isMandatory: mandatoryFilter,
    setIsMandatory: setMandatoryFilter,
    brandIds: brandFilters,
    setBrandIds: setBrandFilters,
    fromDate: campaignFromDateFilter,
    setFromDate: setCampaignFromDateFilter,
    toDate: campaignToDateFilter,
    setToDate: setCampaignToDateFilter,
    resetFilters,
  } = useCampaignsFilters();

  useEffect(() => {
    if (status === 'error') {
      console.error('Error fetching campaigns:', status);
      toast.error(t('campaign.error.genericError'));
    }
  }, [status, t, toast]);

  const createCampaignDialog = useDisclosure(false);
  const { isOpen: showFilters, onToggle: toggleFilters } = useDisclosure(true);

  const [campaignAction, setCampaignAction] = useState<ActionState>();

  const deleteCampaign = useCallback((campaign: CampaignBase<BaseCampaignItem>) => {
    setCampaignAction({ campaign, action: 'Delete' });
  }, []);

  return (
    <Paper
      paddingY="dense"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: 0,
        pt: 2,
        position: 'relative',
        zIndex: 0,
      }}
    >
      <CampaignToolbar
        openCreateDialog={canCreateCampaign ? createCampaignDialog.onOpen : undefined}
      />

      <DiagonalMosaicImgBg />

      <ToggleFiltersToolbar sx={{ justifyContent: 'space-between' }}>
        <CampaignFilterChips
          datePreset={datePreset}
          setDatePreset={setDatePreset}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          campaignType={campaignTypeFilter}
          setCampaignType={setCampaignTypeFilter}
          isMandatory={mandatoryFilter}
          setIsMandatory={setMandatoryFilter}
          selectedBrandIds={brandFilters}
          setSelectedBrandIds={setBrandFilters}
          availableBrands={availableBrandsForChosenTenant}
          fromDate={campaignFromDateFilter}
          toDate={campaignToDateFilter}
          setFromDate={setCampaignFromDateFilter}
          setToDate={setCampaignToDateFilter}
          resetFilters={resetFilters}
          sx={{ pb: 1 }}
        />
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box>
            <Typography component="span" variant="body2">
              {`${t('myAssortment.showing')} `}
            </Typography>
            <Typography component="span" variant="body2" fontWeight={600}>
              {campaigns.length ?? 0}
            </Typography>
            <Typography component="span" variant="body2">
              {` ${t('myAssortment.of')} `}
            </Typography>
            <Typography component="span" variant="body2" fontWeight={600}>
              {campaigns.length ?? 0}
            </Typography>
            <Typography component="span" variant="body2">
              {` ${
                campaigns.length === 1
                  ? t('campaign.campaignSingular').toLowerCase()
                  : t('campaign.campaignPlural').toLowerCase()
              }`}
            </Typography>
          </Box>
          <ToggleFiltersToolbarButton showFilters={showFilters} toggleFilters={toggleFilters} />
        </Stack>
      </ToggleFiltersToolbar>

      <Stack flexDirection="row" flexGrow={1}>
        <CampaignsTable
          campaigns={campaigns}
          isLoading={isLoading}
          language={language}
          columns={campaignsTableColumns}
          deleteCampaign={deleteCampaign}
          getCampignHref={(campaign) =>
            type === 'member'
              ? mgPrisRoutes.campaigns.campaignType.campaignDetails.getFullLinkPath({
                  campaignId: campaign.id,
                  campaignType: campaign.campaignType,
                })
              : mgPrisRoutes.campaigns.campaignDetails.getFullLinkPath({ campaignId: campaign.id })
          }
        />

        <CampaignFilters
          selectedDatePreset={datePreset}
          selectDatePreset={setDatePreset}
          brands={availableBrandsForChosenTenant}
          searchTerm={searchQuery}
          disableFields={isLoading}
          showFilters={showFilters}
          mandatoryFilter={mandatoryFilter}
          campaignTypeFilter={campaignTypeFilter}
          brandFilters={brandFilters}
          campaignFromDateFilter={campaignFromDateFilter}
          campaignToDateFilter={campaignToDateFilter}
          setCampaignTypeFilter={setCampaignTypeFilter}
          setCampaignFromDateFilter={setCampaignFromDateFilter}
          setCampaignToDateFilter={setCampaignToDateFilter}
          setSearchTerm={setSearchQuery}
          setBrandFilters={setBrandFilters}
          setMandatoryFilter={setMandatoryFilter}
          campaignTypeOptions={campaignTypeOptionsMap[type]}
          availableFilters={availableFilters}
        />
      </Stack>

      {createCampaignDialog.isOpen &&
        (isBrandContext ? (
          <CreateBrandCampaignContainer open onClose={createCampaignDialog.onClose} />
        ) : (
          <CreateMemberCampaignContainer open onClose={createCampaignDialog.onClose} />
        ))}

      {campaignAction?.action === 'Delete' && (
        <CampaignDeleteConfirmationContainer
          campaign={campaignAction.campaign}
          open
          onClose={() => setCampaignAction(undefined)}
          type={type}
        />
      )}
    </Paper>
  );
};
